<script lang="ts">
import { defineComponent, inject, ref, watch } from "vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import Zelle from "@/components/grid/Zelle.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zeile from "@/components/grid/Zeile.vue";
import Infotext from "@/components/Infotext.vue";
import Bauartklasse from "@/views/vgv/modals/Bauartklasse.vue";
import Modal from "@/components/Modal.vue";
import Logger from "js-logger";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "BauartklasseErmittlung",
  components: {
    Zeile,
    ValidierungsFehler,
    Zelle,
    TitelZelle,
    Infotext,
    Modal,
    Bauartklasse,
  },
  props: {
    fehler: Object,
  },
  setup() {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");

    const showables = ref({
      toggleBauart: false,
    });

    function toggleBauartCheck() {
      showables.value.toggleBauart = !showables.value.toggleBauart;
    }

    function isNichtFertighausBauart() {
      return (
        piniaStore.state.gebaeudedaten.bauart == "0001" ||
        piniaStore.state.gebaeudedaten.bauart == "0002" ||
        piniaStore.state.gebaeudedaten.bauart == "0003"
      );
    }

    function setFertighausBauart() {
      if (isNichtFertighausBauart()) {
        piniaStore.state.gebaeudedaten.bauart = "0006";
        piniaStore.state.gebaeudedaten.dachbelag = "0";
      }
    }

    function isFertighausBauart() {
      return (
        piniaStore.state.gebaeudedaten.bauart == "0006" ||
        piniaStore.state.gebaeudedaten.bauart == "0007" ||
        piniaStore.state.gebaeudedaten.bauart == "0008"
      );
    }

    function setNichtFertighausBauart() {
      if (isFertighausBauart()) {
        piniaStore.state.gebaeudedaten.bauart = "0001";
      }
    }

    watch(
      () => piniaStore.state.gebaeudedaten.fertighaus,
      (neu) => {
        if (!piniaStore.state.loading) {
          Logger.debug(
            "Fertighaus watch vor Änderung",
            piniaStore.state.gebaeudedaten.fertighaus,
            piniaStore.state.gebaeudedaten.bauart,
            piniaStore.state.loading,
          );
          if (neu === true) {
            setFertighausBauart();
          } else {
            setNichtFertighausBauart();
          }

          Logger.debug(
            "Fertighaus watch nach Änderung",
            piniaStore.state.gebaeudedaten.fertighaus,
            piniaStore.state.gebaeudedaten.bauart,
            piniaStore.state.loading,
          );
        }
      },
    );

    return {
      piniaStore,
      wechsleSichtbarkeit,
      istSichtbar,
      showables,
      toggleBauartCheck,
    };
  },
});
</script>

<template>
  <zeile>
    <titel-zelle :zeige-info="true" info-box-id="fertighaus">
      Handelt es sich um ein <strong>Fertighaus?</strong>
    </titel-zelle>
    <zelle class="mkRadio">
      <input
        v-model="piniaStore.state.gebaeudedaten.fertighaus"
        type="radio"
        id="nichtfertighaus"
        name="fertighaus"
        :value="false"
        required
        aria-required="true"
        aria-label="Nein, es handelt sich nicht um ein Fertighaus"
        aria-describedby="fertighausInfo"
        data-fehlermeldung="Bitte treffen Sie eine Auswahl."
        data-cy="fertighausRadioNo"
      />
      <label for="nichtfertighaus" class="radiolabel">Nein</label>
      <input
        v-model="piniaStore.state.gebaeudedaten.fertighaus"
        type="radio"
        id="fertighaus"
        name="fertighaus"
        :value="true"
        aria-label="Ja, es handelt sich um ein Fertighaus"
        aria-describedby="fertighausInfo"
        data-cy="fertighausRadioYes"
      />
      <label for="fertighaus" class="radiolabel">Ja</label>
      <validierungs-fehler :fehler-text="fehler.fertighaus" :reduzierter-abstand="true" />
      <infotext
        role="tooltip"
        id="fertighausInfo"
        name="fertighaus"
        css-klassen="o-grid__cell--no-space"
        text="Als Fertighaus bezeichnet man ein Haus, das industriell in einem
            Werk nahezu komplett vorgefertigt wird. Es wird in Einzelteilen auf
            die Baustelle geliefert und dort endmontiert."
      >
      </infotext>
    </zelle>
  </zeile>
  <zeile>
    <titel-zelle :zeige-info="true" info-box-id="bauart"><strong>Bauart</strong> des Gebäudes</titel-zelle>
    <zelle>
      <select
        id="bauart"
        name="bauart"
        v-model="piniaStore.state.gebaeudedaten.bauart"
        class="c-form-control-select"
        aria-label="Bauart des Gebäudes"
        data-fehlermeldung="Bitte treffen Sie eine Auswahl."
        data-cy="bauart"
      >
        <option selected v-if="piniaStore.state.gebaeudedaten.fertighaus === false" value="0001">Massiv</option>
        <option v-if="piniaStore.state.gebaeudedaten.fertighaus === false" value="0002">
          Stahl- oder Holzfachwerk mit Stein-/Glasfüllung
        </option>
        <option v-if="piniaStore.state.gebaeudedaten.fertighaus === false" value="0003">
          Stahl- oder Holzfachwerk mit anderem Füllmaterial
        </option>
        <option v-if="piniaStore.state.gebaeudedaten.fertighaus === true" value="0006">
          Alle Teile feuerbeständig
        </option>
        <option v-if="piniaStore.state.gebaeudedaten.fertighaus === true" value="0007">
          Leichtbauweise mit feuerhemmender Ummantelung
        </option>
        <option v-if="piniaStore.state.gebaeudedaten.fertighaus === true" value="0008">
          Leichtbauweise ohne feuerhemmende Ummantelung
        </option>
      </select>
      <validierungs-fehler :fehler-text="fehler.bauart" :reduzierter-abstand="true" />
      <!-- Den folgenden Infoblock nicht als Komponente aufgebaut, da Umsetzung des event-Aufrufs
      zu zeitintensiv wäre -->
      <div v-if="istSichtbar('bauart')" class="gev19__contexthelp-text o-grid__cell--no-space">
        Die Bauart bestimmt die Bauartklasse des Gebäudes und hat somit Einfluss auf die Beitragshöhe. Bei gemischter
        Bauweise ist die weniger massive Bauart zu wählen. Details zu den verschiedenen Bauarten finden Sie
        <a @click.stop="toggleBauartCheck">hier.</a>
      </div>
    </zelle>
  </zeile>
  <zeile>
    <titel-zelle :zeige-info="true" info-box-id="dachbelag"> Art des <strong>Dachbelags</strong></titel-zelle>

    <zelle class="mkRadio">
      <input
        v-model="piniaStore.state.gebaeudedaten.dachbelag"
        type="radio"
        id="dachbelagHart"
        name="dachbelag"
        value="0"
        data-fehlermeldung="Bitte treffen Sie eine Auswahl."
        required
        aria-required="true"
        aria-label="Hartes Dach"
        aria-describedby="dachbelag"
        :disabled="piniaStore.state.gebaeudedaten.fertighaus === true"
      />
      <label for="dachbelagHart" class="radiolabel">Hart</label>
      <input
        v-model="piniaStore.state.gebaeudedaten.dachbelag"
        type="radio"
        id="dachbelagWeich"
        name="dachbelag"
        value="1"
        aria-label="Weiches Dach"
        aria-describedby="dachbelag"
        :disabled="piniaStore.state.gebaeudedaten.fertighaus === true"
      />
      <label for="dachbelagWeich" class="radiolabel">Weich</label>
      <validierungs-fehler :fehler-text="fehler.dachbelag" :reduzierter-abstand="true" />
      <infotext
        role="tooltip"
        id="dachbelag"
        name="dachbelag"
        css-klassen="o-grid__cell--no-space"
        text="Harte Bedachung wie z.B. Ziegel, weiche Bedachung wie z. B. Holz oder Reet (Schilf)."
      >
      </infotext>
    </zelle>
  </zeile>

  <modal v-if="showables.toggleBauart" @closeModal="toggleBauartCheck" titel="Bauart- / Fertighausklassen">
    <template v-slot:inhalt>
      <Bauartklasse />
    </template>
  </modal>
</template>
